import React, { useState, useCallback, Fragment } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { isBlank, notBlank } from "../../util/ext";
import { useConfirmation } from "../../common/use-Confirmation";

import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";

import FallbackImage from "../../components/FallbackImage";
import FileUploadInput from "components/FileUploadInput";
import Image from "../../components/Image";
import ImageCrop from "./ImageCrop";
import MediaDialog from "./MediaDialog";
import { validateThumbnailSize } from "../../util/assetUtil";

const imgSize = 350;

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexDirection: "row",
    maxWidth: 800,
  },

  preview: {
    marginRight: theme.spacing(4),
    display: "flex",
    alignItems: "center",
  },

  image: {
    boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.15)",
  },

  thumbnailOverlay: {
    height: imgSize,
    width: imgSize,
    position: "relative",
    borderRadius: "20px",
    boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.15)",
    backgroundColor: "#E5E5E5",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },

  emptyThumbnail: {
    fontSize: "120px",
    color: theme.palette.primary.disabled,
  },

  input: {
    flexGrow: 1,
    width: 250,
  },

  title: {
    fontSize: "1.6rem",
    lineHeight: "1.7rem",
    color: theme.palette.primary.darker,
    fontWeight: "bold",
    marginBottom: theme.spacing(2),
  },

  subtitle: {
    fontSize: "1.2rem",
    marginBottom: 0,
  },

  fileTypes: {
    fontSize: "1.2rem",
    marginTop: 0,
    marginBottom: theme.spacing(2),
    fontStyle: "italic",
  },
}));

const ThumbnailDialog = ({
  currentUrl,
  accept,
  aspectRatio,
  onCancel,
  onSave,
  visible,
  uploading,
  media = null,
}) => {
  const classes = useStyles();
  const [error, setError] = useState();
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [cropper, setCropper] = useState(null);

  const handleCropperInitialized = cropper => {
    setCropper(cropper);
  };

  const onFileInputChange = file => {
    setThumbnailFile(file);
    if (notBlank(file)) {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const dataUrl = reader.result;
          await validateThumbnailSize(media, dataUrl);
          setThumbnailUrl(dataUrl);
        } catch (err) {
          setThumbnailFile(null);
          setThumbnailUrl(null);
          setError(err.message);
        }
      };
      reader.readAsDataURL(file);
      setError(null);
    } else {
      setError(null);
      setThumbnailUrl(null);
    }
  };

  const handleSave = useCallback(() => {
    if (isBlank(thumbnailFile) && isBlank(currentUrl)) {
      setError("An image must be selected");
    } else {
      const url = cropper.getCroppedCanvas().toDataURL();
      cropper.getCroppedCanvas().toBlob(blob => {
        const file = new File([blob], thumbnailFile.name, {
          lastModified: thumbnailFile.lastModified,
        });
        onSave(url, file);
      });
    }
  }, [cropper, currentUrl, thumbnailFile, onSave]);

  const { showConfirmation, ConfirmationDialog } = useConfirmation({
    title: "Confirm Image Upload",
    message:
      "This save will immediately replace the associated image.  Are you sure you want to continue?",
    confirmButtonText: "Yes",
    onConfirm: handleSave,
  });

  const handleCancel = () => {
    onCancel();
    setError(null);
    setThumbnailFile(null);
    setThumbnailUrl(null);
  };

  return (
    <MediaDialog
      open={visible}
      maxWidth="lg"
      title="Edit Thumbnail"
      handleClose={handleCancel}
      content={
        <div className={classes.root}>
          <ConfirmationDialog />
          <div className={classes.preview}>
            {uploading ? (
              <CircularProgress />
            ) : isBlank(currentUrl) ? (
              notBlank(thumbnailFile) ? (
                <ImageCrop
                  aspectRatio={aspectRatio}
                  imgSize={imgSize}
                  thumbnailUrl={thumbnailUrl}
                  currentUrl={currentUrl}
                  cropper={cropper}
                  handleInitialized={handleCropperInitialized}
                />
              ) : (
                <FallbackImage
                  className={classes.thumbnailOverlay}
                  height={imgSize}
                  width={imgSize}
                />
              )
            ) : notBlank(thumbnailFile) ? (
              <ImageCrop
                aspectRatio={aspectRatio}
                imgSize={imgSize}
                thumbnailUrl={thumbnailUrl}
                currentUrl={currentUrl}
                cropper={cropper}
                handleInitialized={handleCropperInitialized}
              />
            ) : (
              <Image
                className={classes.thumbnailOverlay}
                height={imgSize}
                width={imgSize}
                alt="Thumbnail"
                src={thumbnailUrl || `${currentUrl}&tc=${Date.now()}`}
              />
            )}
          </div>
          <div className={classes.input}>
            <div className={classes.title}>Upload a new thumbnail</div>
            <p className={classes.subtitle}>
              Choose the image you wish to upload.{" "}
              {media?.mediaType === "collection"
                ? "Image height should be at least 264px."
                : "The image should be 264px x 264px."}
            </p>
            <p className={classes.fileTypes}>
              Accepted file types:{" "}
              {accept ||
                ".jpg, .jpeg, .png, .gif, image/jpeg, image/png, image/gif"}
            </p>
            <FileUploadInput
              value={thumbnailFile}
              accept={
                notBlank(accept)
                  ? accept
                  : ".jpg, .jpeg, .png, .gif, image/jpeg, image/png, image/gif"
              }
              onChange={onFileInputChange}
              disabled={uploading}
              error={error}
            />
          </div>
        </div>
      }
      actions={
        <Fragment>
          <Button disabled={uploading} onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            disabled={uploading}
            variant="contained"
            color="primary"
            onClick={showConfirmation}
          >
            Save
          </Button>
        </Fragment>
      }
    />
  );
};

export default ThumbnailDialog;
